import React from 'react'
import { Link } from 'react-router-dom'
import { toDashboardSentenceCase } from '@utils/dashboardText.util'
import { captureDashboardScroll } from '@utils/dashboardReturnNavigation.util'
import DashboardSectionEmptyState from './DashboardSectionEmptyState.component'
import DashboardSectionCardSkeleton, {
  type DashboardSectionCardSkeletonProps,
} from './DashboardSectionCardSkeleton.component'
import { useDashboardLayoutEdit } from './DashboardLayoutEditContext'
import { useDashboardEditableRowSlot } from './DashboardEditableRowSlotContext'
import './DashboardSectionCard.style.css'

type DashboardSectionCardProps = {
  children?: React.ReactNode
  className?: string
  title?: string
  titleHref?: string
  showTitleChevron?: boolean
  headerAfterTitle?: React.ReactNode
  headerRight?: React.ReactNode
  /** When true, the card body shows a shimmer placeholder instead of `children`. */
  loading?: boolean
  /** Optional layout hints for the in-card skeleton body. */
  skeleton?: DashboardSectionCardSkeletonProps
  /** When true, the card body shows the shared empty-state instead of `children`. */
  isEmpty?: boolean
  /** When true, the title is not a link to the detail page (no data to open). */
  disableTitleLink?: boolean
}

function DashboardSectionCard({
  children,
  className = '',
  title,
  titleHref,
  showTitleChevron = true,
  headerAfterTitle,
  headerRight,
  loading = false,
  skeleton,
  isEmpty = false,
  disableTitleLink = false,
}: DashboardSectionCardProps): React.ReactElement {
  const layoutEditMode = useDashboardLayoutEdit()
  const { inSlot, multiSectionLayout } = useDashboardEditableRowSlot()
  const showLayoutGrip = Boolean(layoutEditMode && inSlot && multiSectionLayout)
  // Header-right content (trend / goal / filters) describes data, so hide it
  // while loading and when the card body shows the empty state.
  const showHeaderRight = !loading && !isEmpty
  const showHeader = Boolean(
    title ||
      headerAfterTitle ||
      (showHeaderRight && headerRight) ||
      showLayoutGrip,
  )
  const titleIsLinked =
    Boolean(titleHref) && !loading && !isEmpty && !disableTitleLink
  const titleContent = title ? (
    <>
      <h6 className="dashboard-section-title">
        {toDashboardSentenceCase(title)}
      </h6>
      {showTitleChevron && titleIsLinked && (
        <i
          className="mgc mgc_right_line dashboard-section-title-icon"
          aria-hidden
        />
      )}
    </>
  ) : null

  const boxClassName = [
    'dashboard-section-box',
    isEmpty && !loading ? 'dashboard-section-box--empty' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const contentClassName = [
    'dashboard-section-content',
    loading ? 'dashboard-section-content--skeleton' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={boxClassName}>
      {showHeader && (
        <div className="dashboard-section-header">
          <div className="dashboard-section-header-start">
            {titleContent &&
              (titleIsLinked && titleHref ? (
                <Link
                  to={titleHref}
                  className="dashboard-section-title-link"
                  onClick={(event) => {
                    event.stopPropagation()
                    captureDashboardScroll(titleHref)
                  }}
                >
                  {titleContent}
                </Link>
              ) : (
                <span className="dashboard-section-title-static">
                  {titleContent}
                </span>
              ))}
            {!loading && headerAfterTitle ? (
              <div className="dashboard-section-header-after-title">
                {headerAfterTitle}
              </div>
            ) : null}
          </div>
          <div className="dashboard-section-header-end">
            {showHeaderRight ? headerRight : null}
            {showLayoutGrip && (
              <span
                className="dashboard-section-layout-grip"
                title="Layout edit mode"
                aria-label="Layout edit mode"
              >
                <i className="mgc mgc_dots_fill" aria-hidden />
              </span>
            )}
          </div>
        </div>
      )}
      <div className={contentClassName}>
        {loading ? (
          <DashboardSectionCardSkeleton {...skeleton} />
        ) : isEmpty ? (
          <DashboardSectionEmptyState />
        ) : (
          children
        )}
      </div>
    </div>
  )
}

export default DashboardSectionCard
